<template>
  <div>
    <div class="ibox">
      <div class="ibox-title">
        <h5>Automated Emails</h5>
        <div class="ibox-tools">
          <el-tooltip class="item" content="Add new recipient" placement="left">
            <el-button
              type="primary"
              size="small"
              class="ibox-tool-btn"
              @click="manageRecipient(null)"
              plain
            >
              <i class="fa fa-plus"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip v-if="me.is_staff" class="item" content="Generate Report" placement="left">
            <el-button
              type="primary"
              size="small"
              class="ibox-tool-btn"
              @click="generateReport"
              plain
            >
              <i class="fa fa-envelope"></i>
            </el-button>
          </el-tooltip>
        </div>
      </div>
      <div class="ibox-content ibox-heading">
        Manage who can receive automated campaign, survey and programming dashboard reports.
      </div>
      <div class="ibox-content user-list-content">
        <div class="input-group">
          <input type="text" class="input form-control" placeholder="Search..." v-model="keyword" />
          <span class="input-group-btn">
            <button class="btn btn-primary"><i class="fa fa-search"></i> Search</button>
          </span>
        </div>
        <br />
        <el-table
          v-loading="loading"
          :data="results"
          :fit="true"
          :empty-text="
            !results.length && recipients.length
              ? `No recipients found matching ${keyword}`
              : 'No recipients'
          "
          style="width: 100%"
          max-height="400"
          stripe
        >
          <el-table-column label="Name">
            <template slot-scope="scope">
              <div>{{ `${scope.row.firstName} ${scope.row.lastName}` }}</div>
            </template>
          </el-table-column>
          <el-table-column label="Email" prop="email"> </el-table-column>
          <el-table-column label="Company" v-if="companies.length">
            <template slot-scope="scope">
              {{ scope.row.company.title }}
            </template>
          </el-table-column>
          <el-table-column label="Reports" align="left">
            <template slot-scope="scope">
              <div class="client-status center">
                <el-tooltip content="Campaign Summary" placement="top">
                  <span v-if="scope.row.campaignSummary" class="label label-info">&nbsp;</span>
                </el-tooltip>
                <el-tooltip content="Survey Summary" placement="top">
                  <span v-if="scope.row.surveySummary" class="label label-warning-light"
                    >&nbsp;</span
                  >
                </el-tooltip>
                <el-tooltip content="Programming Dashboard" placement="top">
                  <span v-if="scope.row.programmingSummary" class="label label-success"
                    >&nbsp;</span
                  >
                </el-tooltip>
                <span
                  v-if="
                    !scope.row.campaignSummary &&
                      !scope.row.surveySummary &&
                      !scope.row.programmingSummary
                  "
                  ><i>None</i></span
                >
              </div>
            </template>
          </el-table-column>
          <el-table-column align="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="manageRecipient(scope.row)">Edit</el-button>
              <el-button size="mini" type="danger" @click="deleteRecipient(scope.row)"
                >Delete</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-dialog
      v-if="showRecipientManage"
      :title="
        selectedRecipient.id ? 'Edit Automated Email Recipient' : 'New Automated Email Recipient'
      "
      :visible.sync="showRecipientManage"
      :close-on-click-modal="false"
    >
      <recipient-manage
        v-if="showRecipientManage"
        :recipient="selectedRecipient"
        :users="users"
        :me="me"
        :companies="companies"
        @cancelled="showRecipientManage = false"
        @saved="handleSaved"
        @deleted="handleDelete"
        @error="handleError"
      />
    </el-dialog>

    <el-dialog
      v-if="showGenerator"
      title="Generate Automated Email"
      :visible.sync="showGenerator"
      :close-on-click-modal="false"
    >
      <generate-report
        :loading="generatingReport"
        :surveys="surveys"
        :campaigns="campaigns"
        @generate="handleGenerate($event)"
        @cancel="showGenerator = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import generateReport from '@/graphql/automatedemails/emails/generateEmail.gql';
import deleteRecipientMutation from '@/graphql/automatedemails/recipients/deleteRecipient.gql';
import RecipientManage from './RecipientManage';
import GenerateReport from './GenerateReport';

export default {
  name: 'EmailRecipientList',
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    companies: {
      type: Array,
      default: () => [],
    },
    surveys: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    recipients: {
      type: Array,
      default: () => [],
    },
    me: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    RecipientManage,
    GenerateReport,
  },
  data: () => ({
    keyword: '',
    selectedRecipient: null,
    showRecipientManage: false,
    showGenerator: false,
    generatingReport: false,
  }),
  computed: {
    showCompanyColumn() {
      return this.me.is_org_admin || this.me.is_staff || this.me.is_superuser;
    },
    results() {
      const recipients = this.recipients.filter(
        recipient =>
          this.find(this.keyword, recipient.firstName) ||
          this.find(this.keyword, recipient.lastName) ||
          this.find(this.keyword, recipient.email),
      );
      return recipients.length ? recipients : [];
    },
  },
  methods: {
    find(keyword, str) {
      return str.toLowerCase().indexOf(keyword.toLowerCase()) > -1;
    },
    generateReport() {
      this.showGenerator = true;
    },
    handleGenerate(report) {
      this.generatingReport = true;
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: generateReport,
          variables: report,
        })
        .then(() => {
          this.showGenerator = false;
          this.$notify({
            title: 'Success!',
            message: 'Email successfully sent to your inbox!',
            type: 'success',
          });
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            message: 'Unable to generate report. Please contact support.',
            type: 'error',
          });
        })
        .finally(() => {
          this.generatingReport = false;
        });
    },
    manageRecipient(recipient) {
      // for some reason ElementUI does not use the latest user object from the list.
      // If a user was updated, and then edited again, the form populated with old data.
      // Therefore we get the user with the same ID from the list. This should be the updated user.
      this.selectedRecipient = this.recipients.find(x => recipient && x.id === recipient.id) || {
        firstName: null,
        lastName: null,
        email: null,
        campaignSummary: false,
        programmingSummary: false,
        surveySummary: false,
      };

      this.showRecipientManage = true;
    },
    handleSaved() {
      this.showRecipientManage = false;
      this.$emit('saved');
    },
    handleError() {},
    handleDelete() {
      this.$emit('deleted');
    },
    deleteRecipient(recipient) {
      this.selectedRecipient = recipient;
      this.$confirm(
        `Are you sure you want to stop ${recipient.firstName} ${recipient.lastName} from receiving all automated emails?`,
      )
        .then(() => {
          this.$apollo
            .mutate({
              client: 'apiv2',
              mutation: deleteRecipientMutation,
              variables: { id: parseInt(recipient.id, 10) },
            })
            .then(() => this.handleDelete());
        })
        .catch(() => null); // prevent uncaught exeptions warning in console...
    },
  },
};
</script>

<style lang="scss" scoped>
.user-list-content {
  padding: 8px;
}

.ibox-tool-btn {
  margin-top: -10px;
}

.user-legend {
  padding: 5px;
}

.client-status {
  margin-right: 15px;

  .label {
    font-size: 8px;
    font-family: monospace;
    padding: 0 2px;
  }
}

.user-avatar {
  height: 25px;
  width: auto;
  border-radius: 13px;
}

.el-dropdown {
  &.v-leave,
  &.v-leave-active {
    .el-dropdown-item .menu-title,
    .el-dropdown__title .menu-title {
      display: none;
    }
  }
}
.el-dropdown-item,
.el-dropdown__title {
  &:hover {
    color: #ffffff !important;
  }
}

.el-dropdown-link {
  cursor: pointer;
  color: #409eff;
}
.el-icon-arrow-down {
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// EmailRecipientList.vue?dc3ef184